import axios, { type AxiosResponse } from 'axios'

import { API_ENDPOINTS } from '@/constants'

import type {
    InternalOrderGraphicFileDeliveryMethodsResponseSchema,
    InternalOrderNumberConfigurationTypesResponseSchema,
    InternalOrderProductionFileAttachment,
    InternalOrderResponseSchema,
    InternalOrderStatusesResponseSchema,
    InternalOrdersAddCommentPayloadSchema,
    InternalOrdersAddCommentResponseSchema,
    InternalOrdersAddProductionFileLinkPayloadSchema,
    InternalOrdersCopyProductAttachmentsPayloadSchema,
    InternalOrdersCopyProductAttachmentsResponseSchema,
    InternalOrdersCreateOrderPayloadSchema,
    InternalOrdersListResponseSchema,
    InternalOrdersUpdateOrderPayloadSchema
} from '@/api/schemas/orders/internal'

const PRODUCT_FILE_UPLOAD = API_ENDPOINTS.ORDERS.PRODUCT_FILE_UPLOAD
const ORDER_FILE_UPLOAD = API_ENDPOINTS.ORDERS.ORDER_FILE_UPLOAD

class InternalOrdersQueries {
    fetchOrders(queryString = ''): Promise<AxiosResponse<InternalOrdersListResponseSchema>> {
        const url = `${API_ENDPOINTS.ORDERS.LIST}${queryString ? (queryString.startsWith('?') ? queryString : `?${queryString}`) : ''}`
        return axios.get<InternalOrdersListResponseSchema>(url)
    }

    fetchOrder(orderId: number | string): Promise<AxiosResponse<InternalOrderResponseSchema>> {
        return axios.get<InternalOrderResponseSchema>(`${API_ENDPOINTS.ORDERS.DETAILS}/${orderId}`)
    }

    fetchOrderStatuses(): Promise<AxiosResponse<InternalOrderStatusesResponseSchema>> {
        return axios.get<InternalOrderStatusesResponseSchema>(API_ENDPOINTS.ORDERS.STATUSES)
    }

    createOrder(orderData: InternalOrdersCreateOrderPayloadSchema): Promise<AxiosResponse<InternalOrderResponseSchema>> {
        return axios.post<InternalOrderResponseSchema>(API_ENDPOINTS.ORDERS.COLLECTOR, orderData)
    }

    updateOrder(payload: InternalOrdersUpdateOrderPayloadSchema): Promise<AxiosResponse<InternalOrderResponseSchema>> {
        return axios.patch<InternalOrderResponseSchema>(`${API_ENDPOINTS.ORDERS.COLLECTOR}/${payload.id}`, payload)
    }

    deleteOrder(orderId: number | string): Promise<AxiosResponse<unknown>> {
        return axios.delete(`${API_ENDPOINTS.ORDERS.COLLECTOR}/${orderId}`)
    }

    fetchNumberConfigurationTypes(): Promise<AxiosResponse<InternalOrderNumberConfigurationTypesResponseSchema>> {
        return axios.get<InternalOrderNumberConfigurationTypesResponseSchema>(API_ENDPOINTS.ORDERS.NUMBER_CONFIGURATION_TYPES)
    }

    fetchOrderGraphicFileDeliveryMethods(): Promise<AxiosResponse<InternalOrderGraphicFileDeliveryMethodsResponseSchema>> {
        return axios.get<InternalOrderGraphicFileDeliveryMethodsResponseSchema>(
            API_ENDPOINTS.ORDERS.GRAPHIC_FILE_DELIVERY_METHODS
        )
    }

    addComment(payload: InternalOrdersAddCommentPayloadSchema): Promise<AxiosResponse<InternalOrdersAddCommentResponseSchema>> {
        return axios.post<InternalOrdersAddCommentResponseSchema>(API_ENDPOINTS.ORDERS.ORDER_COMMENTS, payload)
    }

    uploadProductionFile(formData: FormData): Promise<AxiosResponse<InternalOrderProductionFileAttachment>> {
        return axios.post<InternalOrderProductionFileAttachment>(PRODUCT_FILE_UPLOAD, formData, { timeout: 900000 })
    }

    addProductionFileLink(
        payload: InternalOrdersAddProductionFileLinkPayloadSchema
    ): Promise<AxiosResponse<InternalOrderProductionFileAttachment>> {
        return axios.post<InternalOrderProductionFileAttachment>(PRODUCT_FILE_UPLOAD, payload)
    }

    deleteProductionFile(attachmentId: number | string): Promise<AxiosResponse<unknown>> {
        return axios.delete(`${PRODUCT_FILE_UPLOAD}/${attachmentId}`)
    }

    uploadDeliveryAttachment(formData: FormData): Promise<AxiosResponse<unknown>> {
        return axios.post(ORDER_FILE_UPLOAD, formData)
    }

    copyOrderProductAttachments(
        payload: InternalOrdersCopyProductAttachmentsPayloadSchema
    ): Promise<AxiosResponse<InternalOrdersCopyProductAttachmentsResponseSchema>> {
        return axios.post<InternalOrdersCopyProductAttachmentsResponseSchema>(
            API_ENDPOINTS.ORDERS.COPY_ORDER_PRODUCT_ATTACHMENTS,
            payload
        )
    }
}

export default new InternalOrdersQueries()
